// Pure math utilities for audio processing. No browser APIs — safe to use in tests.

/**
 * Downsample a Float32Array of audio samples to `width` peak values.
 * Returns the maximum absolute value in each segment.
 */
export function downsamplePeaks(data, width) {
  const len = data.length;
  const spp = len / width;
  const peaks = new Float32Array(width);
  for (let px = 0; px < width; px++) {
    const s = Math.floor(px * spp);
    const e = Math.min(Math.ceil((px + 1) * spp), len);
    let max = 0;
    for (let i = s; i < e; i++) {
      const v = Math.abs(data[i]);
      if (v > max) max = v;
    }
    peaks[px] = max;
  }
  return peaks;
}

/**
 * Convert RMS amplitude to dB. Returns -60 for silence (rms <= 0).
 */
export function rmsToDb(rms) {
  return rms > 0 ? 20 * Math.log10(rms) : -60;
}

/**
 * Convert gain (0–2) to a dB string with sign, e.g. "+3.0 dB" or "−2.4 dB".
 */
export function gainToDbStr(gain) {
  if (gain <= 0) return '−∞ dB';
  const db = 20 * Math.log10(gain);
  const sign = db >= 0 ? '+' : '−';
  return `${sign}${Math.abs(db).toFixed(1)} dB`;
}

/**
 * Combine multiple per-stem peak arrays (Float32Array of equal length) into a
 * single array of element-wise maxima. Used to render a single combined
 * waveform across all backing stems.
 */
export function mixPeaksMax(arrays) {
  if (!arrays.length) return new Float32Array(0);
  const n = arrays[0].length;
  const out = new Float32Array(n);
  for (const arr of arrays) {
    for (let i = 0; i < n; i++) {
      if (arr[i] > out[i]) out[i] = arr[i];
    }
  }
  return out;
}

/**
 * Compute peak array (width values) from multi-channel audio data.
 * Takes max(|sample|) across all channels in each bin.
 * Equivalent to the legacy `computePeaks(AudioBuffer, width)` but operates
 * on raw Float32Array channels, so it is safe to run in a Worker.
 * @param {Float32Array[]} channels  one Float32Array per channel
 * @param {number} width             number of output peak values
 * @returns {Float32Array}
 */
export function multiChannelPeaks(channels, width) {
  if (!channels.length || width <= 0) return new Float32Array(0);
  const len   = channels[0].length;
  const spp   = len / width;
  const peaks = new Float32Array(width);
  for (let px = 0; px < width; px++) {
    const s = Math.floor(px * spp);
    const e = Math.min(Math.ceil((px + 1) * spp), len);
    let max = 0;
    for (let c = 0; c < channels.length; c++) {
      const d = channels[c];
      for (let i = s; i < e; i++) {
        const v = Math.abs(d[i]);
        if (v > max) max = v;
      }
    }
    peaks[px] = max;
  }
  return peaks;
}

/**
 * Asymmetric peak reduction: per output bin, return the signed min and max
 * sample value across all channels. Used for DAW-style ribbon waveforms where
 * negative excursions render below the centre line. All-negative content yields
 * `{min: <neg>, max: <less-neg>}`, NOT `{min:0,max:0}` — sentinels are ±Infinity
 * so the first sample replaces them.
 * @param {Float32Array[]} channels  one Float32Array per channel
 * @param {number} width             number of output bins
 * @returns {{minPeaks: Float32Array, maxPeaks: Float32Array}}
 */
export function multiChannelPeaksMinMax(channels, width) {
  const empty = new Float32Array(0);
  if (!channels.length || width <= 0) return { minPeaks: empty, maxPeaks: empty };
  const len      = channels[0].length;
  const spp      = len / width;
  const minPeaks = new Float32Array(width);
  const maxPeaks = new Float32Array(width);
  for (let px = 0; px < width; px++) {
    const s = Math.floor(px * spp);
    const e = Math.min(Math.ceil((px + 1) * spp), len);
    let mn = Infinity, mx = -Infinity;
    for (let c = 0; c < channels.length; c++) {
      const d = channels[c];
      for (let i = s; i < e; i++) {
        const v = d[i];
        if (v < mn) mn = v;
        if (v > mx) mx = v;
      }
    }
    minPeaks[px] = isFinite(mn) ? mn : 0;
    maxPeaks[px] = isFinite(mx) ? mx : 0;
  }
  return { minPeaks, maxPeaks };
}

/**
 * Reduce or replicate `{minPeaks, maxPeaks}` to a target width.
 * - Source > target → take min(min)/max(max) over each source range.
 * - Source === target → return the input object unchanged (zero-copy ref).
 * - Source < target → replicate each source bin (poor man's upsample).
 * @param {{minPeaks: Float32Array, maxPeaks: Float32Array}} src
 * @param {number} width
 */
export function downsamplePeaksMinMax(src, width) {
  const empty = new Float32Array(0);
  if (width <= 0 || !src?.minPeaks || !src?.maxPeaks) return { minPeaks: empty, maxPeaks: empty };
  const srcLen = src.minPeaks.length;
  if (srcLen === width) return src;

  const minOut = new Float32Array(width);
  const maxOut = new Float32Array(width);
  const spp = srcLen / width;
  for (let px = 0; px < width; px++) {
    const s = Math.floor(px * spp);
    const e = Math.min(Math.max(s + 1, Math.ceil((px + 1) * spp)), srcLen);
    let mn = Infinity, mx = -Infinity;
    for (let i = s; i < e; i++) {
      if (src.minPeaks[i] < mn) mn = src.minPeaks[i];
      if (src.maxPeaks[i] > mx) mx = src.maxPeaks[i];
    }
    minOut[px] = isFinite(mn) ? mn : 0;
    maxOut[px] = isFinite(mx) ? mx : 0;
  }
  return { minPeaks: minOut, maxPeaks: maxOut };
}

/**
 * Combine multiple per-stem `{minPeaks, maxPeaks}` arrays additively
 * (element-wise sum). Captures dynamics correctly when ≥1 track is
 * constant-loud (metronome, heavily-compressed master) — element-wise
 * max would lock the overview at ±1 across the whole timeline.
 *
 * Output can exceed ±1 (sum of N tracks). Caller should normalize to
 * the rendering range (e.g. normalizePeaks(out, 0.85)).
 *
 * @param {{minPeaks: Float32Array, maxPeaks: Float32Array}[]} arr
 */
export function mixPeaksSum(arr) {
  const empty = new Float32Array(0);
  if (!arr?.length) return { minPeaks: empty, maxPeaks: empty };
  const n = arr[0].minPeaks.length;
  const minOut = new Float32Array(n);
  const maxOut = new Float32Array(n);
  for (let s = 0; s < arr.length; s++) {
    const mn = arr[s].minPeaks;
    const mx = arr[s].maxPeaks;
    for (let i = 0; i < n; i++) {
      minOut[i] += mn[i];
      maxOut[i] += mx[i];
    }
  }
  return { minPeaks: minOut, maxPeaks: maxOut };
}

/**
 * Combine multiple per-stem `{minPeaks, maxPeaks}` arrays element-wise:
 * `min(min)` / `max(max)`. Used for the overview-strip combined waveform.
 * @param {{minPeaks: Float32Array, maxPeaks: Float32Array}[]} arr
 */
export function mixPeaksMinMax(arr) {
  const empty = new Float32Array(0);
  if (!arr?.length) return { minPeaks: empty, maxPeaks: empty };
  const n = arr[0].minPeaks.length;
  const minOut = new Float32Array(n);
  const maxOut = new Float32Array(n);
  minOut.set(arr[0].minPeaks);
  maxOut.set(arr[0].maxPeaks);
  for (let s = 1; s < arr.length; s++) {
    const mn = arr[s].minPeaks;
    const mx = arr[s].maxPeaks;
    for (let i = 0; i < n; i++) {
      if (mn[i] < minOut[i]) minOut[i] = mn[i];
      if (mx[i] > maxOut[i]) maxOut[i] = mx[i];
    }
  }
  return { minPeaks: minOut, maxPeaks: maxOut };
}

/**
 * Pure parser for the BWPK binary peak sidecar generated by the recorder.
 * Layout (see app/audio-engine/recorder/peaks-stream.js):
 *   Header (20 B): "BWPK" magic (4) + version (1) + channels (1) +
 *                  reserved (2) + sampleRate (4 LE) + samplesPerBin (2 LE) +
 *                  reserved (2) + numBins (4 LE)
 *   Body: per bin 8 B = [min:f32 LE, max:f32 LE]
 *   numBins === 0xFFFFFFFF means unfinalised placeholder.
 *
 * Throws errors with `.code` set:
 *   - BWPK_INVALID      — bad magic / wrong input type / header too short
 *   - BWPK_UNFINALIZED  — numBins is the placeholder
 *   - BWPK_TRUNCATED    — buffer shorter than declared body
 *
 * @param {ArrayBuffer | Uint8Array} input
 * @returns {{minPeaks: Float32Array, maxPeaks: Float32Array,
 *            sampleRate: number, samplesPerBin: number, numBins: number}}
 */
export function parseBwpk(input) {
  let view;
  if (input instanceof ArrayBuffer) {
    view = new DataView(input);
  } else if (input instanceof Uint8Array) {
    view = new DataView(input.buffer, input.byteOffset, input.byteLength);
  } else {
    const e = new Error('BWPK_INVALID: input must be ArrayBuffer or Uint8Array');
    e.code = 'BWPK_INVALID';
    throw e;
  }

  if (view.byteLength < 20 ||
      view.getUint8(0) !== 0x42 || view.getUint8(1) !== 0x57 ||
      view.getUint8(2) !== 0x50 || view.getUint8(3) !== 0x4B) {
    const e = new Error('BWPK_INVALID: magic bytes not BWPK');
    e.code = 'BWPK_INVALID';
    throw e;
  }

  const sampleRate    = view.getUint32(8, true);
  const samplesPerBin = view.getUint16(12, true);
  const numBins       = view.getUint32(16, true);

  if (numBins === 0xFFFFFFFF) {
    const e = new Error('BWPK_UNFINALIZED: numBins is placeholder; recording not finalised');
    e.code = 'BWPK_UNFINALIZED';
    throw e;
  }

  const expectedBytes = 20 + numBins * 8;
  if (view.byteLength < expectedBytes) {
    const e = new Error(`BWPK_TRUNCATED: expected ${expectedBytes} bytes for numBins=${numBins}, got ${view.byteLength}`);
    e.code = 'BWPK_TRUNCATED';
    throw e;
  }

  const minPeaks = new Float32Array(numBins);
  const maxPeaks = new Float32Array(numBins);
  for (let i = 0; i < numBins; i++) {
    minPeaks[i] = view.getFloat32(20 + i * 8,     true);
    maxPeaks[i] = view.getFloat32(20 + i * 8 + 4, true);
  }
  return { minPeaks, maxPeaks, sampleRate, samplesPerBin, numBins };
}

export function dbToGain(db) {
  if (!isFinite(db)) return 0;
  return Math.pow(10, db / 20);
}

/**
 * Find the sample-time of the first audible transient across all channels.
 * Used by the import flow to suggest a barOffsetMs that aligns the song's
 * bar grid with the first audible event in an imported click/backing track.
 *
 * Returns ms-offset of the first sample whose absolute amplitude crosses
 * `threshold`. Returns null when:
 *   - no sample crosses threshold within `searchLimitMs` (silent / sub-threshold);
 *   - detected ms is below `minLeadInMs` (track already aligned, sub-perceptual);
 *   - detected ms exceeds `maxLeadInMs` (probably not a click track — ambient pad,
 *     long fade-in, etc; spurious trigger).
 *
 * @param {Float32Array[]} channels  one Float32Array per channel
 * @param {number}         sampleRate
 * @param {{threshold?:number, minLeadInMs?:number, maxLeadInMs?:number, searchLimitMs?:number}} [opts]
 * @returns {number | null}  ms-offset rounded to integer, or null
 */
export function detectFirstTransient(channels, sampleRate, opts = {}) {
  const threshold     = opts.threshold     ?? 0.05;
  const minLeadInMs   = opts.minLeadInMs   ?? 3;
  const maxLeadInMs   = opts.maxLeadInMs   ?? 2000;
  const searchLimitMs = opts.searchLimitMs ?? 5000;

  if (!channels?.length || !sampleRate) return null;
  const limit = Math.min(channels[0].length, Math.floor(sampleRate * searchLimitMs / 1000));

  let earliestIdx = -1;
  for (let c = 0; c < channels.length; c++) {
    const data = channels[c];
    const upper = (earliestIdx === -1) ? limit : Math.min(limit, earliestIdx);
    for (let i = 0; i < upper; i++) {
      if (Math.abs(data[i]) > threshold) {
        earliestIdx = i;
        break;
      }
    }
  }

  if (earliestIdx < 0) return null;
  const ms = Math.round((earliestIdx * 1000) / sampleRate);
  if (ms < minLeadInMs || ms > maxLeadInMs) return null;
  return ms;
}
